import { Facepile } from "@dropbox/dig-components/avatar";
import { Truncate } from "@dropbox/dig-components/truncate";
import { Text } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import * as React from "react";
import { Attendee } from "../attendee";
import type { CalendarEvent, CalendarEventLabels } from "../types";
import styles from "./CalendarEventCard.module.css";

export type LabeledEventData = {
  id: string;
  label: string;
  content: React.ReactNode;
};

type CalendarEventCardLabels = Pick<
  CalendarEventLabels,
  | "getNumAttendeesLabel"
  | "getFormattedEventTime"
  | "getRelativeTime"
  | "timeLabel"
  | "attendingLabel"
  | "descriptionLabel"
  | "getFullDate"
  | "showMore"
  | "showLess"
  | "guestLabel"
  | "hostLabel"
  | "relativeTimeLabel"
>;

export const useLabeledEventDataGrid = (
  event: CalendarEvent,
  labels: CalendarEventCardLabels,
): LabeledEventData[] => {
  const formattedTime = React.useMemo(() => {
    return labels.getFormattedEventTime({
      startTime: event.startTime,
      endTime: event.endTime,
      isAllDay: event.allDay,
    });
  }, [labels, event.startTime, event.endTime, event.allDay]);

  const eventDate = React.useMemo(
    () => new Date(event.startTime),
    [event.startTime],
  );

  const fullDate = labels.getFullDate(eventDate);

  const relativeTime = labels.getRelativeTime({ startTime: event.startTime });

  // When we have no description let's update the UI to show additional grid info
  const noDescriptionUI = event.sanitizedDescription?.trim() === "";

  return React.useMemo(() => {
    const items: LabeledEventData[] = [];

    // Time item
    items.push({
      id: "time",
      label: labels.timeLabel,
      content: <Text size="large">{formattedTime}</Text>,
    });

    if (noDescriptionUI) {
      items.push({
        id: "when",
        label: labels.relativeTimeLabel,
        content: <Text size="large">{relativeTime}</Text>,
      });
    }

    // Attendees/When item
    if (!noDescriptionUI && event.attendees && event.attendees.length > 0) {
      items.push({
        id: "attendees",
        label: labels.attendingLabel,
        content: (
          <div className={styles.attendeesFlex}>
            <Attendee
              attendees={event.attendees}
              labels={{
                getNumAttendeesLabel: labels.getNumAttendeesLabel,
              }}
              isLargeText={true}
            />
          </div>
        ),
      });
    }

    // Additional items for no description variation
    if (noDescriptionUI) {
      // Guest item
      if (event.attendees && event.attendees.length > 0) {
        items.push({
          id: "guest",
          label: labels.guestLabel,
          content: (
            <Attendee
              attendees={event.attendees}
              labels={{
                getNumAttendeesLabel: labels.getNumAttendeesLabel,
              }}
            />
          ),
        });
      }

      // Host item
      if (event.creatorEmail) {
        items.push({
          id: "host",
          label: labels.hostLabel,
          content: (
            <Box display="flex" alignItems="center" marginLeft={"Micro Small"}>
              <Facepile size="xsmall" isInteractive={false}>
                <Facepile.Item key={"host-"} alt={`Avatar for host`}>
                  {/* NOTE: for now we don't have full creator display name so defaulting to email */}
                  {event.creatorEmail.charAt(0)}
                </Facepile.Item>
              </Facepile>
              <Text size="large" style={{ width: "100%" }}>
                <Truncate
                  tooltipControlProps={{
                    auto: true,
                    placement: "top",
                  }}
                >
                  {event.creatorEmail}
                </Truncate>
              </Text>
            </Box>
          ),
        });
      }
    }

    return items;
  }, [event, labels, formattedTime, fullDate, noDescriptionUI]);
};
